import { sendToApi } from 'api/client'
import { Opaque, HandbookV2, ViewType } from 'types'

export type FavoriteId = Opaque<number, 'FavoriteId'>
interface BaseFavorite {
  favoritable_emoji: string | null
  favoritable_file_icon: string | null
  favoritable_title: string
  favoritable_channel: string
  favoritable_workspace_id: number
  favoritable_url: string
  favoritable_preview_url: string
  favoritable_children_preview_urls?: string[]
  id: FavoriteId
  position: number
  favoritable_handbook?: Pick<HandbookV2, 'seo_string' | 'custom_path' | 'key'>
  has_edit_access: boolean
  favoritable_parent_id?: string
  handbook_mode?: boolean
  database_mode?: boolean
  default_project?: boolean
  default_view?: ViewType
  created_at?: string
  favorited_at?: string
  updated_at?: string
  owner?: {
    avatar_url?: string
    display_name: string
  }
  has_subprojects?: boolean
  has_descendant_articles?: boolean
  project_id: number | undefined
}

export interface ArticleFavorite extends BaseFavorite {
  favoritable_id: string
  favoritable_type: FavoritableType.Article
  favoritable_slug: string
}

interface ProjectFavorite extends BaseFavorite {
  favoritable_id: string
  favoritable_type: FavoritableType.Project
  favoritable_slug: null
}

interface HandbookFavorite extends BaseFavorite {
  favoritable_id: string
  favoritable_type: FavoritableType.Handbook
  favoritable_slug: string
}

interface MediaFileFavorite extends BaseFavorite {
  favoritable_id: string
  favoritable_type: FavoritableType.MediaFile
  favoritable_slug: string
}

interface DesignFavorite extends BaseFavorite {
  favoritable_id: string
  favoritable_type: FavoritableType.Design
  favoritable_slug: string
}

interface VideoFavorite extends BaseFavorite {
  favoritable_id: string
  favoritable_type: FavoritableType.Video
  favoritable_slug: string
}

export enum FavoritableType {
  Article = 'Article',
  Project = 'Project',
  Handbook = 'Handbook',
  MediaFile = 'MediaFile',
  Design = 'Design',
  Video = 'Video',
}

export type Favorite = ArticleFavorite | ProjectFavorite | HandbookFavorite | MediaFileFavorite | DesignFavorite | VideoFavorite

export const FavoriteClient = {
  createFavorite: <T extends Favorite>(id: T['favoritable_id'], type: T['favoritable_type']) =>
    sendToApi<{ favorite: Favorite }>('/favorites', {
      body: { favorite: { favoritable_id: id, favoritable_type: type } },
      method: 'POST',
    }),
  deleteFavorite: (id: FavoriteId) => sendToApi<void>(`/favorites/${id}`, { method: 'delete' }),
  getFavorites: () => sendToApi<{ favorites: Favorite[] }>('/favorites'),
  insertAt: (id: FavoriteId, position: Favorite['position']) =>
    sendToApi<{ favorites: Favorite[] }>(`/favorites/${id}/insert_at`, {
      body: { insert_at: position },
      method: 'put',
    }),
}
